import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import type { DataResponse, Varianta } from "@/api/schemas";

/** Marža jednej zostavy; `null` = zdediť globálny zisk výpočtu. */
export interface MarzaZostavy {
  zostava_id: string;
  kod: string;
  zisk_pct_override: string | null;
}

export interface MarzeVstup {
  zisk_pct: string;
  sleva_pct: string;
  /** Len **zmenené** zostavy — nezmenené sa neposielajú. */
  zostavy: MarzaZostavy[];
  /**
   * Hodnoty pred uložením — z nich sa skladá undo krok.
   *
   * Musia prísť z volajúceho: hook nevie, čo bolo vo formulári predtým, a
   * čítať to z cache po invalidácii je už neskoro.
   */
  predosle?: {
    zisk_pct: string;
    sleva_pct: string;
    zostavy: MarzaZostavy[];
  };
}

export interface MarzeVysledok {
  hotovo: number;
  /** Kódy zostáv, ktoré neprešli, s dôvodom. */
  chyby: { kod: string; sprava: string }[];
}

/**
 * Uloženie celého pultu na marže — varianta + zmenené zostavy (KOLO 65).
 *
 * Ide to cez **existujúce PATCH endpointy** volané sekvenčne, s invalidáciou
 * **raz na konci** — rovnaký vzor ako `useBulkDielce` (KOLO 58). Transakčný
 * bulk endpoint by musel duplikovať audit, kontrolu unikátneho kódu a
 * prepočet z `PATCH /zostavy/{id}`, a to sa rozíde.
 *
 * Dôsledok, ktorý sa nezakrýva: **nie je to atomické.** Keď zlyhá tretia
 * z piatich zostáv, prvé dve zostanú uložené — preto sa vracia
 * `{hotovo, chyby[]}` a dialóg podľa toho vypíše, čo neprešlo, menovite
 * podľa kódu zostavy.
 *
 * Undo je **jeden krok za celé uloženie**, nie N (bolo to jedno kliknutie) —
 * rovnaké pravidlo ako pri hromadnej úprave dielcov.
 */
export function useUlozMarze(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<MarzeVysledok, Error, MarzeVstup> {
  const qc = useQueryClient();
  const undo = useUndoOptional();

  return useMutation({
    mutationFn: async (vstup: MarzeVstup): Promise<MarzeVysledok> => {
      const chyby: MarzeVysledok["chyby"] = [];
      let hotovo = 0;

      // Varianta prvá: globálny zisk je to, od čoho sa odvíjajú zdedené
      // marže, takže keby zlyhala, riadky by sa počítali od starého globálu.
      await api.patch<DataResponse<Varianta>>(`/varianty/${variantaId}`, {
        zisk_pct: vstup.zisk_pct,
        sleva_pct: vstup.sleva_pct,
      });
      hotovo += 1;

      for (const z of vstup.zostavy) {
        try {
          await api.patch(`/zostavy/${z.zostava_id}`, {
            zisk_pct_override: z.zisk_pct_override,
          });
          hotovo += 1;
        } catch (e) {
          chyby.push({
            kod: z.kod,
            sprava: e instanceof Error ? e.message : "neznáma chyba",
          });
        }
      }
      return { hotovo, chyby };
    },
    onSuccess: (_vysledok, vstup) => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
      qc.invalidateQueries({ queryKey: queryKeys.varianty.cenaNahlady() });
      if (vstup.predosle) {
        const p = vstup.predosle;
        undo?.pridaj({
          popis: "zmenu marží",
          vrat: async () => {
            await api.patch(`/varianty/${variantaId}`, {
              zisk_pct: p.zisk_pct,
              sleva_pct: p.sleva_pct,
            });
            for (const z of p.zostavy) {
              await api.patch(`/zostavy/${z.zostava_id}`, {
                zisk_pct_override: z.zisk_pct_override,
              });
            }
            await qc.invalidateQueries({
              queryKey: queryKeys.zakazky.detail(zakazkaId),
            });
          },
        });
      }
    },
  });
}
